/**
 * Centralized utilities for field ID operations
 * Handles normalization, generation, parsing, and extraction of field IDs
 */

/**
 * Normalize field ID from validationErrors to match input element format. ValidationErrors uses 0-based for documents/packages, +1 for others. Input elements use +1 for all fields
 * 
 * @param {string} fieldId - The field ID to normalize
 * @returns {string} - Normalized field ID
 */
export const normalizeValidationErrorFieldId = (fieldId) => {
  if (!fieldId) return fieldId;
  
  // Handle package marca fields first (most specific): packages_marcas_marca_declarationIndex_itemIndex_embajaIndex_marcaIndex - convert to: packages_marcas_marca_declarationIndex+1_itemIndex_embajaIndex_marcaIndex
  const marcaMatch = fieldId.match(/^packages_marcas_marca_(\d+)_(\d+)_(\d+)_(\d+)$/);
  if (marcaMatch) {
    const [, declIdx, itemIdx, embajaIdx, marcaIdx] = marcaMatch;
    return `packages_marcas_marca_${parseInt(declIdx) + 1}_${itemIdx}_${embajaIdx}_${marcaIdx}`;
  }
  
  // Handle document fields: documents_fieldName_declarationIndex_itemIndex_docIndex - convert to: documents_fieldName_declarationIndex+1_itemIndex_docIndex (match everything after documents_ up to the last three underscore-separated numbers)
  const docMatch = fieldId.match(/^documents_(.+)_(\d+)_(\d+)_(\d+)$/);
  if (docMatch) {
    const [, fieldName, declIdx, itemIdx, docIdx] = docMatch;
    return `documents_${fieldName}_${parseInt(declIdx) + 1}_${itemIdx}_${docIdx}`;
  }
  
  // Handle package fields: packages_fieldName_declarationIndex_itemIndex_embajaIndex - convert to: packages_fieldName_declarationIndex+1_itemIndex_embajaIndex (match everything after packages_ up to the last three underscore-separated numbers)
  const pkgMatch = fieldId.match(/^packages_(.+)_(\d+)_(\d+)_(\d+)$/);
  if (pkgMatch) {
    const [, fieldName, declIdx, itemIdx, embajaIdx] = pkgMatch;
    return `packages_${fieldName}_${parseInt(declIdx) + 1}_${itemIdx}_${embajaIdx}`;
  }
  
  // For other fields (declaration and item fields), they already use +1 format
  return fieldId;
};

/**
 * Generate field ID based on field path and indices
 * Uses declarationIndex + 1 for all fields to match input element format
 * 
 * @param {string} field - The field name/path
 * @param {number} declarationIndex - 0-based declaration index
 * @param {number} [itemIndex] - Optional item index
 * @param {number} [docIndex] - Optional document index
 * @param {number} [embajaIndex] - Optional package/embalaje index
 * @param {number} [marcaIndex] - Optional marca index
 * @returns {string} - Generated field ID
 */
export const getFieldId = (field, declarationIndex, itemIndex, docIndex, embajaIndex, marcaIndex) => {
  // Check if it's a document or package field (has prefix)
  if (field.startsWith('documents_')) {
    // Document field: documents_fieldName_declarationIndex+1_itemIndex_docIndex
    const fieldName = field.replace('documents_', '');
    return `documents_${fieldName}_${declarationIndex + 1}_${itemIndex}_${docIndex}`;
  } else if (field.startsWith('packages_')) {
    // Package field: packages_fieldName_declarationIndex+1_itemIndex_embajaIndex
    let fieldName = field.replace('packages_', '');
    if (fieldName.startsWith('marcas_marca')) {
      // Package marca field: packages_marcas_marca_declarationIndex+1_itemIndex_embajaIndex_marcaIndex
      return `packages_marcas_marca_${declarationIndex + 1}_${itemIndex}_${embajaIndex}_${marcaIndex}`;
    }
    return `packages_${fieldName}_${declarationIndex + 1}_${itemIndex}_${embajaIndex}`;
  }
  
  // Extract the actual field name for regular fields
  let actualFieldName = field;
  if (field.includes('.')) {
    actualFieldName = field.split('.').pop();
  } else if (field.includes('[') && field.includes(']')) {
    actualFieldName = field.split('[')[0];
  }
  
  // Generate ID based on whether it's an item field or declaration field - use != null to treat both null and undefined as "not provided"
  if (itemIndex != null) {
    // Item field: fieldName_declarationIndex+1_itemIndex
    return `${actualFieldName}_${declarationIndex + 1}_${itemIndex}`;
  } else {
    // Declaration field: fieldName_declarationIndex+1
    return `${actualFieldName}_${declarationIndex + 1}`;
  }
};

/**
 * Parse field ID to extract field information
 * 
 * @param {string} fieldId - The field ID to parse
 * @returns {Object|null} - Parsed field info with type, fieldName, and indices, or null if invalid
 */
export const parseFieldId = (fieldId) => {
  if (!fieldId) return null;
  
  // IMPORTANT: Check item fields FIRST (more specific pattern) before declaration fields - item field: fieldName_declarationIndex+1_itemIndex (match everything up to the last two underscore-separated numbers)
  const itemMatch = fieldId.match(/^(.+)_(\d+)_(\d+)$/);
  if (itemMatch && !fieldId.startsWith('documents_') && !fieldId.startsWith('packages_')) {
    const [, fieldName, declNum, itemNum] = itemMatch;
    return {
      type: 'item',
      fieldName,
      declarationIndex: parseInt(declNum) - 1,
      itemIndex: parseInt(itemNum),
      docIndex: undefined,
      embajaIndex: undefined,
      marcaIndex: undefined
    };
  }
  
  // Declaration field: fieldName_declarationIndex+1 - match everything up to the last underscore followed by a number (must come AFTER item field check to avoid false matches)
  const declMatch = fieldId.match(/^(.+)_(\d+)$/);
  if (declMatch && !fieldId.startsWith('documents_') && !fieldId.startsWith('packages_')) {
    const [, fieldName, declNum] = declMatch;
    return {
      type: 'declaration',
      fieldName,
      declarationIndex: parseInt(declNum) - 1,
      itemIndex: undefined,
      docIndex: undefined,
      embajaIndex: undefined,
      marcaIndex: undefined
    };
  }
  
  // Document field: documents_fieldName_declarationIndex+1_itemIndex_docIndex - match documents_ prefix, then field name (may have underscores), then three numbers
  const docMatch = fieldId.match(/^documents_(.+)_(\d+)_(\d+)_(\d+)$/);
  if (docMatch) {
    const [, fieldName, declNum, itemNum, docNum] = docMatch;
    return {
      type: 'document',
      fieldName,
      declarationIndex: parseInt(declNum) - 1,
      itemIndex: parseInt(itemNum),
      docIndex: parseInt(docNum),
      embajaIndex: undefined,
      marcaIndex: undefined
    };
  }
  
  // Package marca field: packages_marcas_marca_declarationIndex+1_itemIndex_embajaIndex_marcaIndex
  const marcaMatch = fieldId.match(/^packages_marcas_marca_(\d+)_(\d+)_(\d+)_(\d+)$/);
  if (marcaMatch) {
    const [, declNum, itemNum, embajaNum, marcaNum] = marcaMatch;
    return {
      type: 'package_marca',
      fieldName: 'marcas_marca',
      declarationIndex: parseInt(declNum) - 1,
      itemIndex: parseInt(itemNum),
      docIndex: undefined,
      embajaIndex: parseInt(embajaNum),
      marcaIndex: parseInt(marcaNum)
    };
  }
  
  // Package field: packages_fieldName_declarationIndex+1_itemIndex_embajaIndex - match packages_ prefix, then field name (may have underscores), then three numbers
  const pkgMatch = fieldId.match(/^packages_(.+)_(\d+)_(\d+)_(\d+)$/);
  if (pkgMatch) {
    const [, fieldName, declNum, itemNum, embajaNum] = pkgMatch;
    return {
      type: 'package',
      fieldName,
      declarationIndex: parseInt(declNum) - 1,
      itemIndex: parseInt(itemNum),
      docIndex: undefined,
      embajaIndex: parseInt(embajaNum),
      marcaIndex: undefined
    };
  }
  
  return null;
};

/**
 * Extract actual field name from field ID
 * Removes prefixes and indices to get the base field name
 * 
 * @param {string} fieldId - The field ID
 * @returns {string} - The extracted field name
 */
export const extractFieldName = (fieldId) => {
  if (!fieldId) return '';
  
  let actualFieldName = fieldId;
  
  // Handle sender_ prefix: sender_fieldName or sender_fieldName_exportNumber
  if (fieldId.startsWith('sender_')) {
    const match = fieldId.match(/^sender_(.+?)(?:_\d+)?$/);
    if (match) {
      actualFieldName = match[1];
    } else {
      actualFieldName = fieldId.replace('sender_', '');
    }
  }
  // Handle documents_ prefix: documents_fieldName_declarationIndex_itemIndex_docIndex
  else if (fieldId.startsWith('documents_')) {
    const match = fieldId.match(/^documents_(.+?)_\d+_\d+_\d+$/);
    if (match) {
      actualFieldName = match[1];
    } else {
      actualFieldName = fieldId.replace('documents_', '');
    }
  }
  // Handle packages_marcas_marca prefix
  else if (fieldId.startsWith('packages_marcas_marca')) {
    actualFieldName = 'marcas_marca';
  }
  // Handle packages_ prefix: packages_fieldName_declarationIndex_itemIndex_embajaIndex
  else if (fieldId.startsWith('packages_')) {
    const match = fieldId.match(/^packages_(.+?)_\d+_\d+_\d+$/);
    if (match) {
      actualFieldName = match[1];
    } else {
      actualFieldName = fieldId.replace('packages_', '');
    }
  }
  // Handle item fields: fieldName_declarationIndex_itemIndex
  else if (fieldId.match(/^.+_\d+_\d+$/)) {
    const match = fieldId.match(/^(.+?)_\d+_\d+$/);
    if (match) {
      actualFieldName = match[1];
    }
  }
  // Handle declaration fields: fieldName_declarationIndex
  else if (fieldId.match(/^.+_\d+$/)) {
    const match = fieldId.match(/^(.+?)_\d+$/);
    if (match) {
      actualFieldName = match[1];
    }
  }
  
  return actualFieldName;
};

/**
 * Extract item number from title string (e.g., "Item #1" -> 0)
 * 
 * @param {string} title - The title string containing item number
 * @returns {number|null} - The item number (0-based) or null if not found
 */
export const extractItemNumber = (title) => {
  if (!title) return null;
  const match = title.match(/#(\d+)/);
  return match ? parseInt(match[1]) - 1 : null;
};

/**
 * Extract document/package number from title string (e.g., "Document #1" -> 0)
 * 
 * @param {string} title - The title string containing document/package number
 * @returns {number|null} - The document/package number (0-based) or null if not found
 */
export const extractDocPkgNumber = (title) => {
  if (!title) return null;
  const match = title.match(/#(\d+)/);
  return match ? parseInt(match[1]) - 1 : null;
};

